import { ABIAppBar, LightOrDark } from '@ab-inbev-labs/ux-react-components';
import { FC, useContext } from 'react';
import { SidebarContext } from '../context/SidebarContext';
import { UserProfile } from './UserProfile';
import { HelpMenu } from './HelpMenu';
import { LogoutButton } from './LogoutButton';
import CollapsibleBreadcrumb from './CollapsibleBreadcrumb';
import { AdditionalAppBarSettingsContent } from './userProfileSettings/types';
import { OfflineIndicator } from './OfflineIndicator';
import { useIsOnline } from '../hooks/useIsOnline';

type AppBarWithBreadcrumbProps = {
  appTitle: string;
  isThisRenderedInvisibleOnWebpagePrint: boolean;
  showUserProfile?: boolean;
  additionalAppBarSettings?: AdditionalAppBarSettingsContent;
};

enum ValidEnv {
  DEV = 'DEV',
  TEST = 'TEST',
  LOCAL = 'LOCAL',
  PREVIEW = 'PREVIEW'
}

const computeEnvClass = (env: ValidEnv) => {
  switch (env) {
    case ValidEnv.DEV:
      return 'env-dev';
    case ValidEnv.TEST:
      return 'env-test';
    case ValidEnv.LOCAL:
      return 'env-local';
    case ValidEnv.PREVIEW:
      return 'env-preview';
    default:
      return '';
  }
};

const computeTitleWithEnv = (appTitle: string, env: ValidEnv | null) => {
  return env ? (
    <span>
      {appTitle} <span className={computeEnvClass(env)}>{env}</span>
    </span>
  ) : (
    appTitle
  );
};

const AppBarWithBreadcrumb: FC<AppBarWithBreadcrumbProps> = ({
  appTitle,
  isThisRenderedInvisibleOnWebpagePrint,
  showUserProfile = true,
  additionalAppBarSettings
}) => {
  const { doesCurrentApplicationUseSidebar } = useContext(SidebarContext);
  const isOnline = useIsOnline();
  const showIsOffline = !isOnline && additionalAppBarSettings?.showNetworkStatus;
  const env = import.meta.env.VITE_ENV;
  const envToShow = /^(DEV|TEST|LOCAL|PREVIEW)$/.test(env) ? env : null;

  const classNames =
    'sam-header ' +
    `${doesCurrentApplicationUseSidebar ? 'sam-header-with-sidebar' : ''} ` +
    `${isThisRenderedInvisibleOnWebpagePrint ? 'no-print' : ''} ` +
    `${showIsOffline ? 'app-is-offline' : ''}`;

  const titleWithEnv = computeTitleWithEnv(appTitle, envToShow as ValidEnv | null);

  return (
    <ABIAppBar
      appTitle={titleWithEnv as string}
      theme={LightOrDark.Light}
      breadcrumb={<CollapsibleBreadcrumb isThisRenderedInvisibleOnWebpagePrint />}
      className={classNames}
      additionalContent={showIsOffline ? <OfflineIndicator /> : undefined}
    >
      <div className="actions-and-offline-indicator">
        <div className="user-profile-actions">
          <HelpMenu additionalHelpMenuContent={additionalAppBarSettings?.additionalHelpMenuContent} />

          {showUserProfile && <UserProfile additionalSettings={additionalAppBarSettings} />}

          {showUserProfile && <LogoutButton />}
        </div>
      </div>
    </ABIAppBar>
  );
};

export default AppBarWithBreadcrumb;
